import { UploadProgress } from "src/types/upload";

/** Ignore deltas closer together than this — they measure noise, not speed. */
const SAMPLE_INTERVAL_MS = 300;
/** Weight of the newest sample; the rest carries over, so the rate reads steadily. */
const SMOOTHING = 0.3;

/**
 * Turns a stream of "bytes sent so far" readings into progress the UI can show:
 * a percentage, a transfer rate and an estimated time remaining.
 */
export const createProgressTracker = (
  total: number,
  onProgress?: (progress: UploadProgress) => void
) => {
  let lastLoaded: number | null = null;
  let lastSampledAt = 0;
  let bytesPerSecond = 0;

  return (loaded: number) => {
    if (!onProgress) return;

    const now = Date.now();

    if (lastLoaded === null) {
      // First reading. On a resumed upload this already accounts for the parts
      // S3 kept, which must not be counted as throughput.
      lastLoaded = loaded;
      lastSampledAt = now;
    } else if (now - lastSampledAt >= SAMPLE_INTERVAL_MS) {
      const seconds = (now - lastSampledAt) / 1000;
      const instantRate = Math.max(0, loaded - lastLoaded) / seconds;

      bytesPerSecond = bytesPerSecond
        ? bytesPerSecond * (1 - SMOOTHING) + instantRate * SMOOTHING
        : instantRate;

      lastLoaded = loaded;
      lastSampledAt = now;
    }

    const remaining = Math.max(0, total - loaded);

    onProgress({
      loaded,
      total,
      percent:
        total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0,
      bytesPerSecond: Math.round(bytesPerSecond),
      remainingSeconds:
        bytesPerSecond > 0 && remaining > 0
          ? Math.round(remaining / bytesPerSecond)
          : null,
    });
  };
};
